// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './Field.module.scss';
import emailListStyles from './EmailListField.module.scss';
import { mergeClasses } from 'util/commons';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { validateEmailAddress } from 'util/validator';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';

interface EmailListFieldProps {
  label: string;
  extraLabel?: React.ReactNode;
  values: string[];
  onEdit?: (emails: string[]) => Promise<void>;
  placeholder?: string;
  maxCount?: number;
  maxLength?: number;
  required?:
    | {
        min: number;
      }
    | boolean;
}

export const EmailListField = ({
  label,
  extraLabel,
  values,
  onEdit,
  placeholder,
  required = false,
  maxCount = 3,
  maxLength = 100,
}: EmailListFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const padToMax = (arr: string[]) => {
    const padded = [...arr];
    while (padded.length < maxCount) {
      padded.push('');
    }
    return padded.slice(0, maxCount);
  };

  const [inputs, setInputs] = useState<string[]>(padToMax(values));
  const [errors, setErrors] = useState<(string | undefined)[]>(
    Array(maxCount).fill(undefined)
  );
  const { i18n, t } = useTranslation();

  useEffect(() => {
    if (isEditing) {
      setErrors(
        inputs.map((input) =>
          input.length > 0 ? validateEmailAddress(input, {}) : undefined
        )
      );
    }
  }, [i18n.language]);

  const rootError = useMemo(() => {
    if (!isEditing) return undefined;

    if (
      required &&
      inputs.filter((input) => input.trim().length > 0).length <
        (typeof required === 'boolean' ? 1 : required.min)
    ) {
      return t('messages.M_007', { field: i18n.t('common.email') });
    }
  }, [t, inputs, required, isEditing]);

  const handleEditClick = () => {
    setInputs(padToMax(values));
    setErrors(Array(maxCount).fill(undefined));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setInputs(padToMax(values));
    setErrors(Array(maxCount).fill(undefined));
    setIsEditing(false);
  };

  const handleChange = (index: number, value: string) => {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);

    const newErrors = [...errors];
    newErrors[index] =
      value.length > 0 ? validateEmailAddress(value, {}) : undefined;
    setErrors(newErrors);
  };

  const hasError = useMemo(
    () => errors.some(Boolean) || Boolean(rootError),
    [errors, rootError]
  );

  const handleSubmit = () => {
    if (hasError) return;

    const nonEmpty = inputs.filter((email) => email.trim().length > 0);
    onEdit?.(nonEmpty).then(() => {
      setIsEditing(false);
    });
  };

  return (
    <div className={mergeClasses(styles.field, emailListStyles.emailListField)}>
      <p className={styles.label}>
        <span>{label}</span>
        {extraLabel && <span>{extraLabel}</span>}
      </p>
      <div className={emailListStyles.row}>
        {isEditing ? (
          <>
            <div
              className={emailListStyles.inputList}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  handleCancel();
                }
              }}
            >
              {inputs.map((input, index) => (
                <div key={index} className={emailListStyles.inputRow}>
                  <input
                    type="text"
                    className={mergeClasses(
                      styles.input,
                      emailListStyles.emailInput,
                      {
                        [styles.inputError]: errors[index] || rootError,
                      }
                    )}
                    value={input}
                    maxLength={maxLength}
                    autoFocus={index === 0}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      } else if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                    placeholder={placeholder}
                  />
                  {errors[index] && (
                    <p className={styles.errorText}>{errors[index]}</p>
                  )}
                </div>
              ))}

              {rootError && <p className={styles.errorText}>{rootError}</p>}
            </div>
            <div
              className={mergeClasses(styles.editIcon, {
                [styles.disabled]: hasError,
              })}
              onMouseDown={(e) => e.preventDefault()}
            >
              <IconCheck size={32} onClick={handleSubmit} disabled={hasError} />
            </div>
          </>
        ) : (
          <>
            <div className={emailListStyles.emailValues}>
              {values.length > 0 ? (
                values.map((email, index) => (
                  <p key={index} title={email}>
                    {email}
                  </p>
                ))
              ) : (
                <div className={emailListStyles.emailInput}>-</div>
              )}
            </div>
            {onEdit && (
              <div className={styles.editIcon}>
                <IconEdit onClick={handleEditClick} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
